import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Copy01Icon, Tick01Icon } from '@hugeicons/core-free-icons'
import { toast } from 'sonner'
import claudeMark from '@/assets/logos/agents/claude.svg'
import codexMark from '@/assets/logos/agents/codex.svg'
import cursorMark from '@/assets/logos/agents/cursor.svg'
import windsurfMark from '@/assets/logos/agents/windsurf.svg'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MCP_EXAMPLE_PROMPTS, MCP_HEADLINE, MCP_SUBLINE } from '@/data/mcpGuidance'
import { cn } from '@/lib/utils'

/**
 * One pattern for every Humblytics MCP surface (hub card, /api hero):
 * eyebrow + Live pill, one headline, one subline, the same client marks,
 * an ink CTA and a copyable "Try asking" panel. The container (Card or
 * `.well` > `.surface-raised`) belongs to the page; everything inside is here.
 */

/** Agent harnesses the MCP is set up for; mono marks invert in dark mode. */
const MCP_CLIENT_MARKS = [
  { name: 'Claude', src: claudeMark, mono: false },
  { name: 'Codex', src: codexMark, mono: false },
  { name: 'Cursor', src: cursorMark, mono: true },
  { name: 'Windsurf', src: windsurfMark, mono: true },
]

export function McpEyebrow({ isLoading = false }: { isLoading?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium leading-4 text-muted-foreground">Humblytics MCP</span>
      {isLoading ? (
        <Skeleton className="h-5 w-11 rounded-md" />
      ) : (
        <span className="inline-flex h-5 shrink-0 items-center gap-[5px] rounded-[6px] bg-muted px-1.5 text-[11.5px] font-medium leading-[14px] text-foreground">
          <span className="size-1.5 shrink-0 rounded-full bg-live" aria-hidden />
          Live
        </span>
      )}
    </div>
  )
}

export function McpHeadline({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className={cn('font-medium text-foreground', size === 'lg' ? 'text-base leading-6' : 'text-sm leading-5')}>
        {MCP_HEADLINE}
      </p>
      <p className="text-xs leading-4 text-muted-foreground">{MCP_SUBLINE}</p>
    </div>
  )
}

export function McpClients() {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs leading-4 text-muted-foreground">
      <span>Works with</span>
      <span className="flex items-center gap-1.5">
        {MCP_CLIENT_MARKS.map((c) => (
          <img key={c.name} src={c.src} alt={c.name} title={c.name} className={cn('size-4', c.mono && 'dark:invert')} />
        ))}
      </span>
      <span>and any MCP client</span>
    </div>
  )
}

/** Ink, never orange: the hub's one orange is Add site, and /api matches the hub. */
export function McpConnectButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <Button
        size="sm"
        className="h-8 rounded-lg bg-foreground px-3 text-[13px] font-medium text-background hover:bg-foreground/90"
        onClick={onClick}
      >
        Connect an assistant
      </Button>
      <span className="text-xs leading-4 text-muted-foreground">Uses one of your API keys</span>
    </div>
  )
}

/** Clipboard API first, execCommand fallback for insecure or embedded contexts. */
async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      /* fall through */
    }
  }
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

/** Example questions, click to copy. `limit` trims the list for narrow cards. */
export function McpTryAsking({
  limit,
  className,
}: {
  limit?: number
  className?: string
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const prompts = limit ? MCP_EXAMPLE_PROMPTS.slice(0, limit) : MCP_EXAMPLE_PROMPTS

  const onCopy = async (id: string, text: string) => {
    if (!(await copyText(text))) {
      toast.error('Could not copy')
      return
    }
    setCopiedId(id)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 2000)
  }

  return (
    <div className={cn('flex flex-col rounded-lg bg-muted/50 px-3 py-2.5 ring-1 ring-border', className)}>
      <span className="pb-1 text-xs leading-4 text-muted-foreground">Try asking · click to copy</span>
      <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-border">
        {prompts.map((p) => {
          const copied = copiedId === p.id
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onCopy(p.id, p.prompt)}
              className="group flex w-full items-center justify-between gap-2 py-2 text-left text-[13px] leading-[18px] text-foreground transition-colors hover:text-foreground/70"
            >
              <span>{p.prompt}</span>
              <HugeiconsIcon
                icon={copied ? Tick01Icon : Copy01Icon}
                size={12}
                className={cn(
                  'shrink-0 transition-opacity',
                  copied ? 'text-foreground' : 'text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
                )}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** The /api hero: same parts as the hub's McpCard, at hero scale. */
export function McpHero({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="well">
      <section className="surface-raised grid grid-cols-1 gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <McpEyebrow />
          <McpHeadline size="lg" />
          <McpClients />
          <McpConnectButton onClick={onConnect} className="mt-auto pt-1" />
        </div>
        <McpTryAsking />
      </section>
    </div>
  )
}
